import { TableVirtuoso } from 'react-virtuoso'
import { ReactComponent as ArrowRight } from '../../asserts/icons/arrowRight.svg'
import TableRow from './TableRow'
import TableTh from './TableTh'
import { ResizeColumnPluginCtrl } from './plugins/resize/useResizeColumnPluginCtrl'
import { MouseEvent } from 'react'
import './table.css'
import ThreeDotsLoader from '../Loaders/ThreeDotsLoader'

export interface ITableField<T extends Record<string, any>, K extends keyof T | string> {
  key: K
  getter: (item: T, key: K) => string | JSX.Element
  sortGetter: (item: T, key: K) => string | number
  header: string | JSX.Element
  width: string

  // true, если сервер по какой-то причине не может выполнять сортировку по этому полю.
  clientSideOrdering?: boolean
}

interface IProps<T extends Record<string, any>, K extends keyof T> {
  fields: ITableField<T, K>[]

  loading: boolean
  items: T[]
  itemKey: K
  totalItemsCount: number

  pageNumber: number
  onPageNumberChange?: (page: number) => void

  itemsPerPage: number
  onItemsPerPageChange?: (newCount: number) => void

  orderingState: Partial<Record<keyof T, -1 | 0 | 1>>
  onOrderingStateChange: (updated: Partial<Record<keyof T, -1 | 0 | 1>>) => void

  handleSelectAll?: (allSelected: boolean) => void
  selected?: Set<K>
  handleSelect?: (key: K, selected: boolean) => void

  onClick?: (item: T, ev?: MouseEvent<HTMLTableCellElement>) => void
  renderItemContextMenu?: (item: T) => JSX.Element
  onSwap: (target: K, dropped: K) => void

  resizePluginCtrl: ResizeColumnPluginCtrl
  allowSelectItemsCountPerPage?: boolean
}

const Table = <T extends Record<string, any>, K extends keyof T>(props: IProps<T, K>) => {
  const renderHeader = () => {
    return (
      <tr>
        {props.fields.map((field, index) => (
          <TableTh
            column={field as any}
            orderingState={props.orderingState[field.key] ?? 0}
            key={field.key.toString()}
            onOrderingStateTrigger={(key: string) =>
              props.onOrderingStateChange({
                ...props.orderingState,
                [key]: props.orderingState[key] === 1 ? -1 : (props.orderingState[key] ?? 0) + 1,
              })
            }
            onSwap={props.onSwap as any}
            index={index}
            resizePluginCtrl={props.resizePluginCtrl}
          />
        ))}

        {props.renderItemContextMenu && <th style={{ width: '18px' }} />}
      </tr>
    )
  }

  const renderItem = (index: number, item: T) => (
    <TableRow
      fields={props.fields}
      selected={props.selected}
      handleSelect={props.handleSelect}
      item={item}
      itemKey={props.itemKey}
      renderItemContextMenu={props.renderItemContextMenu}
      onCLick={props.onClick}
      index={index}
    />
  )

  const renderPageControls = () => {
    const isFirstPage = props.pageNumber === 0
    const isLastPage = props.itemsPerPage * (props.pageNumber + 1) + 1 >= props.totalItemsCount
    const handleNext = () => props.onPageNumberChange && props.onPageNumberChange(props.pageNumber + 1)
    const handlePrev = () => props.onPageNumberChange && props.onPageNumberChange(props.pageNumber - 1)

    return (
      <div className="inline-flex-gap gap8">
        <ArrowRight
          data-disabled={isFirstPage}
          className="tableArrowLeft tableArrow"
          onClick={props.loading ? undefined : handlePrev}
        />
        {!isFirstPage && (
          <div className="tablePageNumberWrapper" onClick={props.loading ? undefined : handlePrev}>
            {props.pageNumber}
          </div>
        )}
        <div className="tablePageNumberWrapper" data-active={true}>
          {props.pageNumber + 1}
        </div>
        {!isLastPage && (
          <div className="tablePageNumberWrapper" onClick={props.loading ? undefined : handleNext}>
            {props.pageNumber + 2}
          </div>
        )}
        <ArrowRight
          data-disabled={isLastPage}
          className="tableArrow"
          onClick={props.loading ? undefined : handleNext}
        />
      </div>
    )
  }

  return (
    <div className="tableContainer">
      <div className="tableWrapper" data-loading={props.loading}>
        <TableVirtuoso
          className="table"
          fixedHeaderContent={renderHeader}
          data={props.items}
          itemContent={renderItem}
          overscan={10}
          style={{ scrollbarWidth: 'thin' }}
        />
        {props.loading && (
          <div className="tableLoaderContainer">
            <ThreeDotsLoader />
          </div>
        )}
      </div>
      <div className="text-nm inline-flex-gap center tableFooter" style={{ gap: '3em' }}>
        <span>
          {props.itemsPerPage * props.pageNumber +
            1 +
            '-' +
            Math.min(props.itemsPerPage * (props.pageNumber + 1), props.totalItemsCount)}{' '}
          из {props.totalItemsCount}
        </span>
        {renderPageControls()}
      </div>
    </div>
  )
}

export default Table
